import React, { useState } from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button, 
  TextField, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem,
  Grid,
  Box,
  Typography
} from '@mui/material';
import apiClient from "../../request/http-common";
import { useMutation, useQuery } from "react-query";

const CreateInvestment = ({ open, handleClose, userId, onSuccess }) => {
  const [formData, setFormData] = useState({
    user_id: userId,
    investment_plan_id: '',
    amount_invested: '',
    duration: '',
    payment_source: 'Funding Wallet',
    status: 'pending'
  });

  const { data: investmentPlans } = useQuery('investment-plans-all', async () => {
    const response = await apiClient.get('/api/investment-plans');
    return response.data.plans;
  });

  const { data: users } = useQuery('users-all', async () => {
    const response = await apiClient.get('/api/users');
    return response.data.users;
  }, {
    enabled: !userId
  });

  const { mutate: createInvestment, isLoading } = useMutation(
    async () => {
      return await apiClient.post('/api/admin/create-investment', formData);
    },
    {
      onSuccess: (res) => {
        if (res.data.status === 'success') {
          localStorage.setItem('notification', JSON.stringify({
            message: res.data.message,
            type: 'success'
          }));
          const event = new Event('newMessage');
          window.dispatchEvent(event);
          handleClose();
          if (onSuccess) onSuccess();
          resetForm();
        }
      },
      onError: (err) => {
        const errorMessage = err.response?.data.message || 'An error occurred';
        localStorage.setItem('notification', JSON.stringify({
          message: errorMessage,
          type: 'error'
        }));
        const event = new Event('newMessage');
        window.dispatchEvent(event);
      }
    }
  );

  const resetForm = () => {
    setFormData({
      user_id: userId || '',
      investment_plan_id: '',
      amount_invested: '',
      duration: '',
      payment_source: 'Funding Wallet',
      status: 'pending'
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePlanSelect = (planId) => {
    const selectedPlan = investmentPlans?.find(plan => plan.id === planId);
    if (selectedPlan && selectedPlan.duration) {
      const durations = selectedPlan.duration.split(',');
      if (durations.length > 0) {
        setFormData(prev => ({
          ...prev,
          investment_plan_id: planId,
          duration: durations[0].trim()
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          investment_plan_id: planId
        }));
      }
    }
  };

  const paymentSources = [
    'Funding Wallet',
    'Withdrawable Balance',
    'Referral Wallet',
    'Profit'
  ];

  const statuses = [
    { value: 'pending', label: 'Pending' },
    { value: 'approved', label: 'Approved' },
    { value: 'declined', label: 'Declined' },
    { value: 'completed', label: 'Completed' }
  ];

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Create New Investment</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <Grid container spacing={2}>
            {!userId && (
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Select User</InputLabel>
                  <Select
                    name="user_id"
                    value={formData.user_id}
                    label="Select User"
                    onChange={handleChange}
                    required
                  >
                    {users?.map((user) => (
                      <MenuItem key={user.id} value={user.id}>
                        {user.name} ({user.email})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}

            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Investment Plan</InputLabel>
                <Select
                  name="investment_plan_id"
                  value={formData.investment_plan_id}
                  label="Investment Plan"
                  onChange={(e) => handlePlanSelect(e.target.value)}
                  required
                >
                  {investmentPlans?.map((plan) => (
                    <MenuItem key={plan.id} value={plan.id}>
                      {plan.name} (Min: ${plan.min_amount} - Max: ${plan.amount_invested})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Amount Invested"
                name="amount_invested"
                type="number"
                value={formData.amount_invested}
                onChange={handleChange}
                required
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Duration"
                name="duration"
                value={formData.duration}
                onChange={handleChange}
                required
                helperText="e.g., 30 Days, 60 Days"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Payment Source</InputLabel>
                <Select
                  name="payment_source"
                  value={formData.payment_source}
                  label="Payment Source"
                  onChange={handleChange}
                  required
                >
                  {paymentSources.map((source) => (
                    <MenuItem key={source} value={source}>
                      {source}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  name="status"
                  value={formData.status}
                  label="Status"
                  onChange={handleChange}
                  required
                >
                  {statuses.map((status) => (
                    <MenuItem key={status.value} value={status.value}>
                      {status.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button 
          onClick={createInvestment} 
          variant="contained" 
          disabled={isLoading}
        >
          {isLoading ? 'Creating...' : 'Create Investment'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateInvestment;

